import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { User, Buildings, EnvelopeSimple, DeviceMobile, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const ROLES = [
  { id: "student", label: "Student", desc: "Attempt exams and earn certificates" },
  { id: "examiner", label: "Examiner", desc: "Create question banks and build exams" },
  { id: "institution_admin", label: "Institution Admin", desc: "Manage people, exams and institution settings" },
  { id: "proctor", label: "Proctor", desc: "Monitor live exam sessions" },
];

export default function CompleteProfile() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "student",
    institution_code: "",
  });
  const [institutions, setInstitutions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/institutions/public").then((r) => setInstitutions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: user.name && !user.name.startsWith("User ") ? user.name : "",
      email: user.email || "",
      mobile: user.mobile ? user.mobile.slice(-10) : "",
      role: user.role || "student",
    }));
  }, [user]);

  // Only auto-bounce when it's a first-time completion flow. In edit mode we
  // let the user see and modify their account details even though they're
  // profile_complete.
  useEffect(() => {
    if (!editMode && user && user.profile_complete) navigate("/app", { replace: true });
  }, [user, navigate, editMode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    if (!user?.mobile && form.mobile.trim() && !/^[6-9]\d{9}$/.test(form.mobile.trim())) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (form.role !== "student" && !form.institution_code.trim()) {
      toast.error("Institution code is required for this role");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (!user?.mobile && form.mobile.trim()) payload.mobile = form.mobile.trim();
      if (form.institution_code.trim()) payload.institution_code = form.institution_code.trim().toUpperCase();
      const r = await api.post("/auth/complete-profile", payload);
      setUser(r.data.user);
      await refresh();
      if (r.data.pending_role_request) {
        toast.info(
          `Role request for "${r.data.pending_role_request.requested_role.replace(/_/g, " ")}" sent to institution admin.`,
          { duration: 5000 },
        );
      } else if (r.data.user.profile_complete) {
        toast.success(editMode ? "Profile updated" : "Profile 100% complete — welcome aboard");
      } else {
        toast.info(`Saved — profile is ${r.data.user.profile_percent}% complete`);
      }
      navigate(editMode ? "/app/profile" : "/app", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const requiresInstitution = form.role !== "student";

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="complete-profile-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
          {user?.mobile && (
            <div className="text-xs text-zinc-500 inline-flex items-center gap-2">
              <DeviceMobile size={12} /> <span className="font-mono">+{user.mobile}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 lg:px-12 py-12">
        {editMode && (
          <button
            type="button"
            onClick={() => navigate("/app/profile")}
            className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-4"
            data-testid="profile-edit-back"
          >
            <ArrowLeft size={12} /> Back to profile
          </button>
        )}
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">
          {editMode ? "Edit account" : `Profile ${user?.profile_percent ?? 0}% complete`}
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
          {editMode ? "Edit your account details" : "Complete your profile"}
        </h1>
        <p className="text-sm text-zinc-500 mb-10">
          {editMode
            ? "Change your name, email, role, or institution. Role upgrades to examiner or admin need approval from an institution admin."
            : "Add a few more details so we can set up the right dashboard and send results to the right place."}
        </p>
        {!editMode && (
          <div className="h-1.5 bg-zinc-100 w-full -mt-6 mb-10" data-testid="profile-progress-bar">
            <div className="h-1.5 bg-zinc-900 transition-[width] duration-500" style={{ width: `${user?.profile_percent ?? 0}%` }} />
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" data-testid="complete-profile-form">
          <Field label="Full name" required icon={<User size={12} />}>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Priya Sharma"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none"
              required
              autoFocus
              data-testid="profile-name-input"
            />
          </Field>

          <Field label="Email" required icon={<EnvelopeSimple size={12} />}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-900 focus:outline-none read-only:bg-zinc-50 read-only:text-zinc-500"
              readOnly={!!user?.email && !editMode}
              required
              data-testid="profile-email-input"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              Used for sign-in, exam results, certificates and password reset.
            </div>
          </Field>

          <Field label="Mobile number" required icon={<DeviceMobile size={12} />}>
            <div className="flex">
              <span className="border border-r-0 border-zinc-300 px-3 py-2.5 text-sm text-zinc-500 bg-zinc-50 font-mono">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })}
                placeholder="98XXXXXXXX"
                readOnly={!!user?.mobile}
                className="w-full border border-zinc-300 px-3 py-2.5 text-sm font-mono focus:border-zinc-900 focus:outline-none read-only:bg-zinc-50 read-only:text-zinc-500"
                data-testid="profile-mobile-input"
              />
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              {user?.mobile ? "To change your number, use Profile → Change mobile." : "10-digit Indian mobile number for exam alerts."}
            </div>
          </Field>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">I am a&nbsp;<span className="text-red-600">*</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="profile-role-grid">
              {ROLES.map((r) => {
                const active = form.role === r.id;
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => setForm({ ...form, role: r.id })}
                    className={`text-left border p-3 transition-colors ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 hover:border-zinc-500"}`}
                    data-testid={`profile-role-${r.id}`}
                  >
                    <div className="text-sm font-semibold">{r.label}</div>
                    <div className={`text-[11px] mt-0.5 ${active ? "text-zinc-300" : "text-zinc-500"}`}>{r.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <Field
            label={`Institution code ${requiresInstitution ? "" : "(optional)"}`}
            icon={<Buildings size={12} />}
            required={requiresInstitution}
          >
            <input
              list="inst-codes"
              value={form.institution_code}
              onChange={(e) => setForm({ ...form, institution_code: e.target.value.toUpperCase() })}
              placeholder="e.g. ACME"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm uppercase tracking-wider font-mono focus:border-zinc-900 focus:outline-none"
              data-testid="profile-institution-input"
            />
            <datalist id="inst-codes">
              {institutions.map((i) => (
                <option key={i.code} value={i.code}>{i.name}</option>
              ))}
            </datalist>
            <div className="text-[11px] text-zinc-500 mt-1">
              {requiresInstitution
                ? "Required — ask your institution admin for the code."
                : "Leave blank to continue as an independent learner."}
            </div>
          </Field>

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-zinc-900 text-white py-3 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
            data-testid="profile-submit-btn"
          >
            {busy ? "Saving…" : "Save & continue"} <ArrowRight size={14} weight="bold" />
          </button>
          {!editMode && (
            <button
              type="button"
              onClick={() => { sessionStorage.setItem("profile_reminder_dismissed", "1"); navigate("/app"); }}
              className="w-full border border-zinc-300 py-3 text-sm font-medium hover:bg-zinc-50 transition-colors"
              data-testid="profile-skip-btn"
            >
              Skip for now
            </button>
          )}
          <div className="text-[11px] text-zinc-500 text-center">
            By continuing you accept our{" "}
            <a href="/terms" className="underline underline-offset-2 font-medium text-zinc-700">Terms</a>
            {" "}and{" "}
            <a href="/privacy" className="underline underline-offset-2 font-medium text-zinc-700">Privacy Policy</a>.
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, required, icon, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 inline-flex items-center gap-1 font-semibold">
        {icon} {label} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
    </label>
  );
}
